import React from 'react';
import axios from 'axios';

const ENDPOINT = process.env.REACT_APP_API_ENDPOINT;

const DOC_TYPES = [
  { value: 'agreement', label: 'User / Testing Agreement' },
  { value: 'privacy', label: 'Privacy Policy' },
  { value: 'cookies', label: 'Cookies Policy' },
  { value: 'terms', label: 'Terms & Conditions' },
];

export default class UserAgreementManager extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      agreements: [],
      loading: false,
      error: '',
      // form
      title: '',
      content: '',
      version: '',
      type: 'agreement',       // 👈 NEW
      // filter
      filterType: '',          // '' = all
      // edit mode
      editingId: null,
    };
  }

  componentDidMount() {
    this.fetchAgreements();
  }

  fetchAgreements = async () => {
    this.setState({ loading: true, error: '' });
    try {
      const { filterType } = this.state;
      const query = filterType ? `?type=${filterType}` : '';
      const res = await axios.get(`${ENDPOINT}/agreement${query}`, {
        withCredentials: true,
      });
      this.setState({ agreements: res.data || [], loading: false });
    } catch (err) {
      console.error(err);
      this.setState({ error: 'Failed to load agreements', loading: false });
    }
  };

  handleInputChange = (e) => {
    const { name, value } = e.target;
    this.setState({ [name]: value });
  };

  handleFilterChange = (e) => {
    this.setState(
      { filterType: e.target.value },
      () => this.fetchAgreements() // refetch with filter
    );
  };

  resetForm = () => {
    this.setState({
      title: '',
      content: '',
      version: '',
      type: 'agreement',
      editingId: null,
    });
  };

  handleSubmit = async (e) => {
    e.preventDefault();
    const { title, content, version, editingId, type } = this.state;

    if (!title || !content) {
      this.setState({ error: 'Title and content are required.' });
      return;
    }

    try {
      if (editingId) {
        // update
        await axios.put(
          `${ENDPOINT}/agreement/${editingId}`,
          { title, content, version, type },
          { withCredentials: true }
        );
      } else {
        // create
        await axios.post(
          `${ENDPOINT}/agreement`,
          { title, content, version, type },
          { withCredentials: true }
        );
      }

      await this.fetchAgreements();
      this.resetForm();
    } catch (err) {
      console.error(err);
      this.setState({ error: 'Failed to save agreement' });
    }
  };

  handleEdit = (ag) => {
    this.setState({
      editingId: ag._id,
      title: ag.title || '',
      content: ag.content || '',
      version: ag.version || '',
      type: ag.type || 'agreement',
      error: '',
    });
  };

  handleDelete = async (id) => {
    if (!window.confirm('Delete this agreement?')) return;
    try {
      await axios.delete(`${ENDPOINT}/agreement/${id}`, {
        withCredentials: true,
      });
      this.fetchAgreements();
    } catch (err) {
      console.error(err);
      this.setState({ error: 'Failed to delete agreement' });
    }
  };

  render() {
    const {
      agreements,
      loading,
      error,
      title,
      content,
      version,
      editingId,
      type,
      filterType,
    } = this.state;

    return (
      <div className="min-h-screen bg-gradient-to-b from-sky-900 to-sky-800 py-10 px-4">
        <div className="max-w-5xl mx-auto">
          <h1 className="text-3xl text-sky-100 font-bold mb-6 text-center">
            Policy / Agreement Manager
          </h1>

          {/* Error */}
          {error && (
            <div className="bg-red-100 text-red-700 px-4 py-2 rounded mb-4">
              {error}
            </div>
          )}

          {/* Filter */}
          <div className="bg-sky-950/40 border border-sky-700 rounded-xl p-4 mb-6 flex gap-4 items-center">
            <label className="text-sky-100 text-sm">Filter by type:</label>
            <select
              name="filterType"
              value={filterType}
              onChange={this.handleFilterChange}
              className="bg-sky-900 border border-sky-700 rounded px-3 py-2 text-sky-50"
            >
              <option value="">All</option>
              {DOC_TYPES.map((d) => (
                <option key={d.value} value={d.value}>
                  {d.label}
                </option>
              ))}
            </select>
          </div>

          {/* Form */}
          <div className="bg-sky-950/40 border border-sky-700 rounded-xl p-6 mb-8">
            <h2 className="text-xl text-sky-100 font-semibold mb-4">
              {editingId ? 'Edit Document' : 'Add New Document'}
            </h2>
            <form onSubmit={this.handleSubmit} className="space-y-4">
              {/* Type */}
              <div>
                <label className="block text-sky-100 mb-1">Document Type</label>
                <select
                  name="type"
                  value={type}
                  onChange={this.handleInputChange}
                  className="w-full bg-sky-900 border border-sky-700 rounded px-3 py-2 text-sky-50"
                >
                  {DOC_TYPES.map((d) => (
                    <option key={d.value} value={d.value}>
                      {d.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sky-100 mb-1">Title</label>
                <input
                  type="text"
                  name="title"
                  value={title}
                  onChange={this.handleInputChange}
                  className="w-full bg-sky-900 border border-sky-700 rounded px-3 py-2 text-sky-50"
                  placeholder="e.g. Caredevo User Testing Agreement"
                />
              </div>

              <div>
                <label className="block text-sky-100 mb-1">Content</label>
                <textarea
                  name="content"
                  value={content}
                  onChange={this.handleInputChange}
                  className="w-full bg-sky-900 border border-sky-700 rounded px-3 py-2 text-sky-50 h-[800px]"
                  placeholder="Write agreement terms here..."
                />
              </div>

              <div>
                <label className="block text-sky-100 mb-1">Version</label>
                <input
                  type="text"
                  name="version"
                  value={version}
                  onChange={this.handleInputChange}
                  className="w-full bg-sky-900 border border-sky-700 rounded px-3 py-2 text-sky-50"
                  placeholder="e.g. 1.0, 1.1"
                />
              </div>

              <div className="flex gap-4">
                <button
                  type="submit"
                  className="bg-sky-500 hover:bg-sky-600 text-white px-6 py-2 rounded shadow"
                >
                  {editingId ? 'Update' : 'Create'}
                </button>
                {editingId && (
                  <button
                    type="button"
                    onClick={this.resetForm}
                    className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded"
                  >
                    Cancel
                  </button>
                )}
              </div>
            </form>
          </div>

          {/* List */}
          <div className="bg-sky-950/30 border border-sky-700 rounded-xl p-6">
            <h2 className="text-xl text-sky-100 font-semibold mb-4">
              Existing Documents
            </h2>

            {loading ? (
              <p className="text-sky-200 text-sm">Loading...</p>
            ) : agreements.length === 0 ? (
              <p className="text-sky-200 text-sm">No documents found.</p>
            ) : (
              <div className="space-y-4">
                {agreements.map((ag) => (
                  <div
                    key={ag._id}
                    className="bg-sky-900/40 border border-sky-700 rounded p-4 flex justify-between gap-4 items-start"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-3">
                        <h3 className="text-sky-50 font-semibold">
                          {ag.title || 'Untitled'}
                        </h3>
                        {ag.type && (
                          <span className="text-xs bg-sky-200/20 border border-sky-300/30 text-sky-50 px-2 py-0.5 rounded-full">
                            {ag.type}
                          </span>
                        )}
                      </div>
                      <p className="text-sky-200 text-sm mt-2 whitespace-pre-line max-h-40 overflow-y-auto">
                        {ag.content}
                      </p>
                      <p className="text-sky-400 text-xs mt-2">
                        Version: {ag.version || '—'}
                      </p>
                    </div>
                    <div className="flex flex-col gap-2">
                      <button
                        onClick={() => this.handleEdit(ag)}
                        className="bg-sky-500 hover:bg-sky-600 text-white px-3 py-1 rounded text-sm"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => this.handleDelete(ag._id)}
                        className="bg-red-500 hover:bg-red-600 text-white px-3 py-1 rounded text-sm"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }
}
